import {
  AtSign, CalendarClock, HeartHandshake, Image, Mail, MapPin,
  Music, ShoppingBag, Ticket, Type, Users, Video,
  type LucideIcon,
} from 'lucide-react'

import type { BlockType } from '@/lib/dashboard'

/**
 * The five ways of arriving at Fuse.
 *
 * The landing page sells one story -- take bookings, sell products, get paid --
 * which is true of a salon and wrong for everybody else. Somebody who just wants
 * a page that is *theirs* reads it and concludes the product is not for them.
 * These are the same product described five ways, one per person who might want
 * it, and `person` is deliberately first: the default page stays written for
 * someone with nothing to sell.
 *
 * Slugs live under `/about/{slug}` rather than at the top level. `lib/routes.ts`
 * spells out why that matters: `fuse.mn/{username}` owns the first path segment,
 * so a route at `/artist` shadows a handle, and the invariant it relies on is
 * that every app segment is also in the backend's `RESERVED_USERNAMES`. `about`
 * is reserved and `artist` is not, so nesting under `about` keeps the guarantee
 * without a database change.
 *
 * Every capability named here is one the product actually has: the block types
 * are the `blockTypeEnum` values in the backend schema, and the plan each
 * persona is told to expect matches the flags in `PLANS`. Commerce blocks
 * (product, booking, ticket, donate) are Pro; embeds, links and text are free.
 */

export type PersonaSlug = 'person' | 'creator' | 'artist' | 'organizer' | 'business'

/** Ordered. `person` leads, because the default page is written for them. */
export const PERSONA_SLUGS: PersonaSlug[] = ['person', 'creator', 'artist', 'organizer', 'business']

export function isPersonaSlug(value: string): value is PersonaSlug {
  return (PERSONA_SLUGS as string[]).includes(value)
}

/**
 * The blocks each persona is shown, named by the icon that depicts them.
 *
 * A subset, not the full twelve: the point of a persona page is that it looks
 * chosen for the reader, and a complete feature list is what the home page is
 * for. `paid` marks the ones behind Pro so the page can say so honestly rather
 * than implying the free plan sells tickets.
 */
export type PersonaBlock = { icon: LucideIcon, key: BlockType, paid?: boolean }

export const PERSONA_BLOCKS: Record<PersonaSlug, PersonaBlock[]> = {
  // Nothing paid. This is the whole argument of the `person` page: the free
  // plan carries unlimited blocks and unlimited embeds, so a page that is
  // purely self-expression never has to be paid for.
  person: [
    { icon: Music, key: 'audio' },
    { icon: Image, key: 'image' },
    { icon: AtSign, key: 'social' },
    { icon: Type, key: 'text' },
  ],
  creator: [
    { icon: Video, key: 'video' },
    { icon: AtSign, key: 'social' },
    { icon: Image, key: 'image' },
    { icon: Mail, key: 'email', paid: true },
  ],
  artist: [
    { icon: Music, key: 'audio' },
    { icon: Video, key: 'video' },
    { icon: ShoppingBag, key: 'product', paid: true },
    { icon: Ticket, key: 'ticket', paid: true },
  ],
  organizer: [
    { icon: Ticket, key: 'ticket', paid: true },
    { icon: MapPin, key: 'map' },
    { icon: CalendarClock, key: 'booking', paid: true },
    { icon: Users, key: 'social' },
  ],
  business: [
    { icon: CalendarClock, key: 'booking', paid: true },
    { icon: ShoppingBag, key: 'product', paid: true },
    { icon: MapPin, key: 'map' },
    { icon: HeartHandshake, key: 'donate', paid: true },
  ],
}

/**
 * Where a persona lives.
 *
 * `person` is the home page rather than a route of its own: it is the default,
 * and giving it both `/` and `/person` would be two URLs for one page. The
 * other four are top-level routes, which is why each is reserved in the
 * backend.
 */
export function personaHref(slug: PersonaSlug): string {
  return slug === 'person' ? '/' : `/${slug}`
}

/**
 * Whether the persona's story needs a paid plan.
 *
 * Read off `PERSONA_BLOCKS` rather than written down twice, so a block moving
 * between plans cannot leave the note behind claiming the wrong thing.
 */
export function needsPaidPlan(slug: PersonaSlug): boolean {
  return PERSONA_BLOCKS[slug].some(block => block.paid)
}
